// src/utils/uniformAuthorityPdf.ts
// -----------------------------------------------------------------------------
// Generates the "Authorisation for Purchase of Uniforms" PDF (per supplier) with
// jsPDF, uploads it to zephyr-files, and returns { name, url } for appendToDocs().
//
// This is the real-PDF replacement for the old authorityDoc.ts HTML/PDF flow: a
// clean PDF (no browser header/footer) that the shelter and responder simply open
// and print. Data comes from the request (ref, child, ordered uniform items,
// approved total) + the suppliers table (doc_name, abn, logo, account #, terms,
// footer) + logos from the public supplier-logos bucket. Layout mirrors the
// approved prototype (docs/weareco-authorisation-prototype.html).

import { getSupplierLogoUrl, uploadFile } from "../data/storage";
import { getUniformSupplier } from "../data/suppliers";
import { customUniformRowCounts, UNIFORM_ITEMS, WINTER_ITEMS } from "./formConstants";
import { logger } from "./logger";

export interface AuthorityItem {
  name: string;
  cost: number;
  qty: number;
}
export interface UniformAuthorityInput {
  refNo: string; // Zephyr reference (e.g. 2026-00130)
  childIndex: number; // 0-based; drives the "Child N" label + upload path
  childLabel: string; // "Female · Year 4 · Boonah State High School"
  supplierValue: string; // request.uniforms.supplier (app enum value)
  items: AuthorityItem[]; // ordered uniform items only (qty > 0)
  schoolName?: string; // child's school — fills a {{school_name}} doc_name (School / P&C)
  shelterName?: string; // shelter — fills a {{shelter_name}} doc_name (Shelter to Buy)
}

const ZEPHYR = {
  name: "Zephyr Education Limited",
  abn: "47 805 738 577",
  logoKey: "zephyr.png",
  accountName: "Zephyr Education Limited",
};

type RGB = [number, number, number];

function money(n: number): string {
  return `$${(Number(n) || 0).toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
function fmtAbn(s: string | null): string {
  const d = String(s || "").replace(/\D/g, "");
  if (d.length === 11)
    return `${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5, 8)} ${d.slice(8, 11)}`;
  return String(s || "");
}

function waitForJsPDF(): Promise<boolean> {
  return new Promise((resolve) => {
    let n = 0;
    const tick = () => {
      if (window.jspdf?.jsPDF) return resolve(true);
      if (++n > 50) return resolve(false);
      setTimeout(tick, 100);
    };
    tick();
  });
}

interface LoadedImage {
  dataUrl: string;
  format: "PNG" | "JPEG";
  w: number;
  h: number;
}
async function loadImage(url: string): Promise<LoadedImage | null> {
  if (!url) return null;
  try {
    const resp = await fetch(url, { cache: "no-store" });
    if (!resp.ok) return null;
    const blob = await resp.blob();
    const dataUrl: string = await new Promise((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => res(String(fr.result));
      fr.onerror = () => rej(new Error("read failed"));
      fr.readAsDataURL(blob);
    });
    const dims = await new Promise<{ w: number; h: number } | null>((res) => {
      const img = new Image();
      img.onload = () => res({ w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => res(null);
      img.src = dataUrl;
    });
    if (!dims) return null;
    const format = dataUrl.startsWith("data:image/png") ? "PNG" : "JPEG";
    return { dataUrl, format, w: dims.w, h: dims.h };
  } catch (e) {
    logger.warn("authority logo load failed:", e);
    return null;
  }
}

function drawLogo(
  doc: JsPdfDoc,
  img: LoadedImage | null,
  x: number,
  y: number,
  maxW: number,
  maxH: number,
  align: "left" | "right"
): void {
  if (!img) return;
  const ar = img.w / img.h || 1;
  let w = maxW;
  let h = w / ar;
  if (h > maxH) {
    h = maxH;
    w = h * ar;
  }
  const dx = align === "right" ? x + (maxW - w) : x;
  doc.addImage(img.dataUrl, img.format, dx, y, w, h);
}

interface RichSeg {
  t: string;
  b?: boolean; // bold this segment
}
// Draw a run of mixed normal/bold text, word-wrapped inside maxW. Returns the y of
// the last line drawn. `measure` advances position WITHOUT drawing — used to size a
// border box before drawing the text into it. (jsPDF has no inline rich text.)
function drawRich(
  doc: JsPdfDoc,
  segments: RichSeg[],
  x0: number,
  y0: number,
  maxW: number,
  lineH: number,
  size: number,
  measure: boolean
): number {
  doc.setFontSize(size);
  let x = x0;
  let y = y0;
  for (const seg of segments) {
    for (const p of seg.t.split(/(\s+)/).filter((s) => s !== "")) {
      doc.setFont("helvetica", seg.b ? "bold" : "normal");
      const w = doc.getTextWidth(p);
      const isSpace = /^\s+$/.test(p);
      if (!isSpace && x + w > x0 + maxW) {
        x = x0;
        y += lineH;
      }
      if (!(isSpace && x === x0)) {
        if (!measure) doc.text(p, x, y);
        x += w;
      }
    }
  }
  return y;
}

export async function generateUniformAuthorityPdf(
  input: UniformAuthorityInput
): Promise<{ name: string; url: string; blob: Blob } | null> {
  const supplier = await getUniformSupplier(input.supplierValue);
  // Only suppliers flagged with a Print Authorisation document generate one (db-46).
  // getUniformSupplier now resolves ANY uniform supplier by name, so gate on the flag —
  // School Locker has the flag (its order-sheet flow is separate + untouched); School /
  // P&C Shop and the reimbursement suppliers do NOT.
  if (!supplier?.has_authority_doc) return null;

  if (!(await waitForJsPDF())) {
    logger.warn("jsPDF not loaded");
    return null;
  }

  const zephyrLogo = await loadImage(getSupplierLogoUrl(ZEPHYR.logoKey));
  const supplierLogo = await loadImage(getSupplierLogoUrl(supplier.logo_path || ""));

  const JsPDF = window.jspdf!.jsPDF;
  const doc = new JsPDF({ unit: "mm", format: "a4" });

  const teal: RGB = [0, 105, 92];
  const green: RGB = [27, 94, 32];
  const ink: RGB = [26, 26, 26];
  const lineC: RGB = [185, 201, 198];
  const labelBg: RGB = [243, 247, 246];
  const white: RGB = [255, 255, 255];

  const pageW = 210;
  const margin = 14;
  const contentW = pageW - margin * 2;
  // doc_name may carry {{school_name}} / {{shelter_name}} placeholders for the
  // Zephyr-owned "generic" suppliers (School / P&C Shop → the child's school;
  // Shelter to Buy → the shelter). Substitute at generation; fall back to the row
  // name if the result is empty.
  const schoolClean = String(input.schoolName || "")
    .split(/\s+—\s+/)[0]
    .trim();
  const supplierName =
    (supplier.doc_name || supplier.name)
      .replace(/\{\{school_name\}\}/g, schoolClean)
      .replace(/\{\{shelter_name\}\}/g, String(input.shelterName || "").trim())
      .trim() || supplier.name;

  // Which authorisation layout to draw (…15). Default = the current form.
  //   invoice_zephyr → School/P&C + Other (school/shop emails Zephyr a tax invoice)
  //   reimbursement  → DFV Service to Buy (Zephyr reimburses the service)
  const template = supplier.doc_template || "supplier_account";
  const isInvoiceZephyr = template === "invoice_zephyr";
  const isReimbursement = template === "reimbursement";

  const fill = (c: RGB) => doc.setFillColor(c[0], c[1], c[2]);
  const draw = (c: RGB) => doc.setDrawColor(c[0], c[1], c[2]);
  const ink0 = (c: RGB) => doc.setTextColor(c[0], c[1], c[2]);

  let y = 14;

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  ink0(ink);
  doc.text("AUTHORISATION FOR PURCHASE OF UNIFORMS", pageW / 2, y, { align: "center" });
  y += 8;

  // FROM / TO
  const rowTop = y;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  ink0(ink);
  doc.text("FROM", margin, rowTop + 8);
  drawLogo(doc, zephyrLogo, margin + 14, rowTop, 42, 14, "left");
  doc.text(ZEPHYR.name, margin + 14, rowTop + 18);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`ABN ${ZEPHYR.abn}`, margin + 14, rowTop + 22.5);

  const toRight = pageW - margin;
  // Supplier logo — right-aligned at the page edge; measure its drawn width so the
  // "TO" label can sit just to its left (supplier logos vary a lot in width).
  let supLogoW = 0;
  if (supplierLogo) {
    const ar = supplierLogo.w / supplierLogo.h || 1;
    let w = 60;
    let h = w / ar;
    if (h > 14) {
      h = 14;
      w = h * ar;
    }
    supLogoW = w;
    doc.addImage(supplierLogo.dataUrl, supplierLogo.format, toRight - w, rowTop, w, h);
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  ink0(ink);
  doc.text("TO", toRight - supLogoW - 4, rowTop + 8, { align: "right" });
  doc.text(supplierName, toRight, rowTop + 18, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const supplierAbn = fmtAbn(supplier.abn);
  doc.text(supplierAbn ? `ABN ${supplierAbn}` : "ABN - Not Advised", toRight, rowTop + 22.5, {
    align: "right",
  });

  // More space above the intro line (see below — the gap below it is halved and
  // moved up here, so the intro sits balanced between the header and total box).
  y = rowTop + 32;

  // Intro
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  ink0(ink);
  const introText = isReimbursement
    ? `This form authorises ${supplierName} to purchase the following items and Zephyr Education Limited will reimburse ${supplierName} up to the TOTAL AMOUNT APPROVED below:`
    : `This form authorises the bearer to place an order for the following items with ${supplierName} on behalf of Zephyr Education Limited.`;
  const introLines = doc.splitTextToSize(introText, contentW);
  doc.text(introLines, margin, y);
  // Halved gap below the intro (was + 3); the freed space was added above.
  y += introLines.length * 4;

  const lw = contentW * 0.55;
  const boxRow = (label: string, value: string | null, valueColor: RGB) => {
    const h = 9;
    fill(labelBg);
    draw(teal);
    doc.rect(margin, y, lw, h, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    ink0(teal);
    const ll = doc.splitTextToSize(label, lw - 6);
    doc.text(ll, margin + 3, y + h / 2 + 1.2 - (ll.length - 1) * 1.6);
    draw(lineC);
    doc.rect(margin + lw + 3, y, contentW - lw - 3, h);
    if (value != null) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      ink0(valueColor);
      doc.text(value, margin + lw + 6, y + h / 2 + 1.4);
    }
    y += h + 2.5;
  };

  boxRow(
    "TOTAL AMOUNT APPROVED (Incl GST)",
    money(input.items.reduce((s, i) => s + (Number(i.cost) || 0) * (Number(i.qty) || 0), 0)),
    teal
  );
  boxRow(
    "Zephyr Reference No. — to be included on Zephyr's 30 day account invoice",
    input.refNo,
    ink
  );

  y += 6;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  ink0(teal);
  doc.text("The section below MUST be completed by the Zephyr registered Organisation.", margin, y);
  y += 5;

  // Date box + expiry banner
  const dbH = 9;
  const dbW = 60;
  draw(ink);
  doc.rect(margin, y, dbW, dbH);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  ink0(ink);
  const issueDate = new Date();
  const issueDay = String(issueDate.getDate()).padStart(2, "0");
  const issueMonth = String(issueDate.getMonth() + 1).padStart(2, "0");
  doc.text(
    `Date : ${issueDay} / ${issueMonth} / ${issueDate.getFullYear()}`,
    margin + 3,
    y + dbH / 2 + 1.4
  );
  fill(green);
  doc.rect(margin + dbW + 3, y, contentW - dbW - 3, dbH, "F");
  ink0(white);
  doc.text(
    "This Authorisation EXPIRES 14 Days from this Date",
    margin + dbW + 6,
    y + dbH / 2 + 1.4
  );
  y += dbH + 2.5;

  boxRow(
    `Phone and/or email to call if ${isReimbursement ? "the Supplier" : supplierName} has questions about the uniform order`,
    null,
    ink
  );
  boxRow("Name of person authorised to place/order and collect uniforms", null, ink);

  y += 5;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  ink0(teal);
  doc.text(
    "This Approval is NOT transferable. If no longer required for this client, please advise Zephyr.",
    margin,
    y
  );
  y += 8;

  // ORDER — shrink the header to fit the content width. A long child label
  // ("Prefer not to say" + a long school name) overflowed the right margin at
  // a fixed 12pt; step the size down to a 9pt floor, then ellipsis-truncate
  // as a last resort so the line can never escape the page.
  doc.setFont("helvetica", "bold");
  ink0(ink);
  const orderText = `ORDER — Child ${input.childIndex + 1}  ·  ${input.childLabel}`;
  let orderSize = 12;
  doc.setFontSize(orderSize);
  while (orderSize > 9 && doc.getTextWidth(orderText) > contentW) {
    orderSize -= 0.5;
    doc.setFontSize(orderSize);
  }
  let orderDrawn = orderText;
  while (doc.getTextWidth(orderDrawn) > contentW && orderDrawn.length > 12) {
    orderDrawn = `${orderDrawn.slice(0, -2).trimEnd()}…`;
  }
  doc.text(orderDrawn, margin, y);
  y += 5;

  // Item table: ITEM | COST | QTY | TOTAL
  const wCost = 28;
  const wQty = 18;
  const wTot = 28;
  const rCost = margin + contentW - wTot - wQty - wCost;
  const rQty = rCost + wCost;
  const rTot = rQty + wQty;
  const rowH = 6.5;

  fill([238, 242, 241]);
  doc.rect(margin, y, contentW, rowH, "F");
  draw(lineC);
  doc.rect(margin, y, contentW, rowH);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  ink0(teal);
  doc.text("ITEM", margin + 2, y + 4.4);
  doc.text("COST", rCost + wCost, y + 4.4, { align: "right" });
  doc.text("QTY", rQty + wQty, y + 4.4, { align: "right" });
  doc.text("TOTAL", rTot + wTot, y + 4.4, { align: "right" });
  y += rowH;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  ink0(ink);
  let grand = 0;
  for (const it of input.items) {
    const lineTotal = (Number(it.cost) || 0) * (Number(it.qty) || 0);
    grand += lineTotal;
    draw([227, 233, 232]);
    doc.rect(margin, y, contentW, rowH);
    doc.text(String(it.name), margin + 2, y + 4.4);
    doc.text(money(it.cost), rCost + wCost, y + 4.4, { align: "right" });
    doc.text(String(it.qty), rQty + wQty, y + 4.4, { align: "right" });
    doc.text(money(lineTotal), rTot + wTot, y + 4.4, { align: "right" });
    y += rowH;
  }
  fill([240, 245, 244]);
  doc.rect(margin, y, contentW, rowH, "F");
  draw(lineC);
  doc.rect(margin, y, contentW, rowH);
  doc.setFont("helvetica", "bold");
  ink0(teal);
  doc.text("TOTAL COST OF UNIFORMS", rQty + wQty, y + 4.4, { align: "right" });
  doc.text(money(grand), rTot + wTot, y + 4.4, { align: "right" });

  // ── Footer (pinned near the bottom) ── The "NOTES FOR PROCESSING" region varies
  // by authorisation template (…15); the heading + PLEASE NOTE + footer message are
  // shared. The alt templates start a touch higher to fit their taller content.
  let fy = template === "supplier_account" ? 250 : 244;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  ink0(ink);
  doc.text(`NOTES FOR PROCESSING AT ${supplierName.toUpperCase()}`, margin, fy);
  fy += 5.5;

  if (isInvoiceZephyr) {
    // School/P&C + Other: they invoice Zephyr directly. Two lines (line 2 starts
    // "For prompt payment…"), inline bold, ~20% larger than the 9pt footer text.
    const zSize = 11;
    const zLineH = 4.8;
    const zMaxW = contentW - 6;
    const line1: RichSeg[] = [
      { t: "Please charge " },
      { t: "up to the approved amount", b: true },
      { t: " to " },
      { t: "Zephyr Education Ltd", b: true },
      { t: " (ABN 47 805 738 577)." },
    ];
    const line2: RichSeg[] = [
      { t: "For prompt payment, email a " },
      { t: "tax-compliant invoice", b: true },
      { t: " — noting the " },
      { t: "GST amount", b: true },
      { t: " (or note " },
      { t: "no GST", b: true },
      { t: "), your " },
      { t: "ABN", b: true },
      { t: " and your " },
      { t: "bank details", b: true },
      { t: " — to " },
      { t: "invoicing@zephyreducation.com.au", b: true },
    ];
    ink0(ink);
    // Measure (dry run) to size the border box, then draw for real into it.
    const topY = fy;
    let mY = drawRich(doc, line1, margin + 3, topY + 5.5, zMaxW, zLineH, zSize, true);
    mY = drawRich(doc, line2, margin + 3, mY + zLineH, zMaxW, zLineH, zSize, true);
    const boxH = mY - topY + 4;
    draw(ink);
    doc.rect(margin, topY, contentW, boxH);
    let ry = drawRich(doc, line1, margin + 3, topY + 5.5, zMaxW, zLineH, zSize, false);
    ry = drawRich(doc, line2, margin + 3, ry + zLineH, zMaxW, zLineH, zSize, false);
    fy = topY + boxH + 3;
  } else if (isReimbursement) {
    // DFV Service to Buy: the service pays and claims reimbursement.
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(
      "To obtain reimbursement from Zephyr Education Limited for the items authorised, you must provide:",
      margin,
      fy
    );
    fy += 5;
    for (const b of [
      "Tax-compliant invoice from your organisation (ABN, GST details, bank account)",
      "Original supplier invoice/receipt",
      "Proof of payment to supplier",
    ]) {
      doc.text(`•  ${b}`, margin + 3, fy);
      fy += 4.5;
    }
    fy += 1;
  } else {
    // supplier_account — the current form (charge to Zephyr's 30-day account).
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const notesLines = doc.splitTextToSize(
      `Please charge an amount UP TO the amount approved above to Zephyr Education Limited's ${supplier.account_terms || "30-day account"}. Details as follows:`,
      contentW
    );
    doc.text(notesLines, margin, fy);
    fy += notesLines.length * 4 + 2;

    draw(ink);
    doc.rect(margin, fy, 95, 12);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Account Name", margin + 3, fy + 4.5);
    doc.text("Account Number", margin + 3, fy + 9);
    doc.setFont("helvetica", "normal");
    doc.text(ZEPHYR.accountName, margin + 42, fy + 4.5);
    doc.text(supplier.zephyr_account_number || "", margin + 42, fy + 9);
    fy += 16;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  ink0(ink);
  const pn = doc.splitTextToSize(
    "PLEASE NOTE: Zephyr Education will NOT be responsible for amounts expended beyond the approved amount.",
    contentW
  );
  doc.text(pn, margin, fy);
  fy += pn.length * 4 + 2;

  if (supplier.footer_message) {
    draw(lineC);
    doc.line(margin, fy, margin + contentW, fy);
    fy += 3.5;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    ink0([68, 68, 68]);
    const fl = doc.splitTextToSize(
      `Important message for the ${supplierName} team member: ${supplier.footer_message}`,
      contentW
    );
    doc.text(fl, margin, fy);
  }

  const pdfBlob = doc.output("blob");
  const safe = supplierName.replace(/[^a-zA-Z0-9]/g, "_");
  const path = `${input.refNo}/docs/Authorisation_${safe}_child${input.childIndex + 1}_${Date.now()}.pdf`;
  // Best-effort store a copy (for the record); the print flow uses the blob below
  // so it still works even if the upload fails.
  const up = await uploadFile(path, pdfBlob, { upsert: true, contentType: "application/pdf" });
  if (up.error) logger.warn("authority PDF upload failed:", up.error.message);
  return {
    name: `Authorisation_${safe}.pdf`,
    // Private bucket (db-168): persist the object PATH; rendering signs it on demand.
    url: up.error ? "" : path,
    blob: pdfBlob,
  };
}

// ── Convenience wrapper for the "Print Authorisation" button ──────────────────
interface UniformItemData {
  qty?: number | string;
  cost?: number | string;
}
interface UniformsData {
  supplier?: string;
  items?: Record<string, UniformItemData>;
  customItems?: Array<{
    name?: string;
    desc?: string;
    qty?: number | string;
    cost?: number | string;
  }>;
}
interface AuthChild {
  gender?: string;
  yearLevel?: string;
  schoolName?: string;
  uniforms?: UniformsData | null;
}
interface AuthRequest {
  refNo?: string | null;
  shelterName?: string | null;
}

// The ordered uniform lines (qty > 0) for one child's uniform order.
export function uniformOrderedItems(uniforms: UniformsData | null | undefined): AuthorityItem[] {
  const out: AuthorityItem[] = [];
  const map = uniforms?.items || {};
  for (const k of [...UNIFORM_ITEMS, ...WINTER_ITEMS]) {
    const it = map[k];
    const qty = Number(it?.qty) || 0;
    if (qty > 0) out.push({ name: k, cost: Number(it?.cost) || 0, qty });
  }
  // Additional Items: the form renders the 5 custom slots as a two-column grid
  // ROW-major (array slot 0 = row 1 left, 1 = row 1 right, ...), but users fill
  // and read the grid DOWN the left column then down the right. List them in
  // that column reading order (even slots, then odd) so a left-column "1, 2, 3"
  // + right-column "4, 5" entry prints as 1..5 on the single-column PDF —
  // display-only, the stored array is untouched.
  const custom = uniforms?.customItems || [];
  const columnOrder = [
    ...custom.filter((_, i) => i % 2 === 0),
    ...custom.filter((_, i) => i % 2 === 1),
  ];
  for (const it of columnOrder) {
    const qty = Number(it?.qty) || 0;
    // Only real line items (identity + qty + cost) print on the authority doc.
    if (customUniformRowCounts(it))
      out.push({ name: it.name || it.desc || "Item", cost: Number(it?.cost) || 0, qty });
  }
  return out;
}

// Generate the authorisation PDF for one child's approved uniform and open it in
// a new tab for printing. Returns false when the supplier has no authorisation
// doc (School Locker etc.) or generation failed — the caller can fall back.
export async function openUniformAuthorityPdf(
  req: AuthRequest,
  child: AuthChild,
  childIndex: number
): Promise<boolean> {
  // School names are stored as "Name — Suburb"; swap that inner em dash for a
  // comma on the doc so the line has ONE dash (the "ORDER — Child N" one).
  const schoolLabel = (child.schoolName || "").replace(/\s+—\s+/g, ", ");
  const label = [child.gender, child.yearLevel, schoolLabel].filter(Boolean).join("  ·  ");
  const res = await generateUniformAuthorityPdf({
    refNo: req.refNo || "",
    childIndex,
    childLabel: label,
    supplierValue: child.uniforms?.supplier || "",
    items: uniformOrderedItems(child.uniforms),
    schoolName: child.schoolName || "",
    shelterName: req.shelterName || "",
  });
  if (res) {
    // Open the PDF data directly (blob URL) so the browser renders it inline in a
    // new tab rather than downloading the stored file.
    const objUrl = URL.createObjectURL(res.blob);
    window.open(objUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
    return true;
  }
  return false;
}
